import { useCallback } from 'react'

import { SortDirection } from '../../../enums/sortDirection'
import { useQueryParams } from '../../../hooks/useQueryParams'
import { getTableColumnIdDirection } from '../../../utils/getTableColumnIdDirection'
import { SuppliersBalanceSortProperty } from '../enums/suppliersBalanceSortProperty'

interface SuppliersBalanceSortQuery {
  direction: SortDirection
  sortBy: SuppliersBalanceSortProperty
}

const defaultQuery: SuppliersBalanceSortQuery = {
  direction: SortDirection.Asc,
  sortBy: SuppliersBalanceSortProperty.AccountNumber,
}

interface SuppliersBalanceSortState {
  direction: SortDirection
  setSort: (columnId: string, isDesc: boolean) => void
  sortBy: SuppliersBalanceSortProperty
  sortedColumnId: string
}

const isSuppliersBalanceSortProperty = (columnId: string): columnId is SuppliersBalanceSortProperty =>
  (Object.values(SuppliersBalanceSortProperty) as string[]).includes(columnId)

export const useSuppliersBalanceSort = (): SuppliersBalanceSortState => {
  const [{ direction, sortBy }, setQuery] = useQueryParams<SuppliersBalanceSortQuery>({ defaultQuery })

  const setSort = useCallback(
    (columnId: string, isDesc: boolean) => {
      if (!isSuppliersBalanceSortProperty(columnId)) {
        return
      }

      setQuery({
        direction: isDesc ? SortDirection.Desc : SortDirection.Asc,
        sortBy: columnId,
      })
    },
    [setQuery],
  )

  const validSortBy = isSuppliersBalanceSortProperty(sortBy) ? sortBy : defaultQuery.sortBy
  const sortedColumnId = getTableColumnIdDirection(validSortBy, direction === SortDirection.Desc) as string

  return { direction, setSort, sortBy: validSortBy, sortedColumnId }
}
